(() => {
  const roots = document.querySelectorAll('[data-cs-locations-root]');
  if (!roots.length) return;

  const WEEKDAYS = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };

  const LOCATION_COLORS = {
    'parmer-ranch-porch-pickup': '#7EA8A6',
    'parmer-ranch-park': '#6F8F6A',
    'arboretum-farmers-market': '#2B3647',
    'bee-cave-farmers-market': '#BE9140'
  };

  const FIXED_COORDINATES = {
    'parmer-ranch-porch-pickup': { lat: 30.735764, lng: -97.786975 },
    'parmer-ranch-park': { lat: 30.738026459677197, lng: -97.793715597013 }
  };

  const RAIN_TYPES = new Set([
    'DRIZZLE',
    'LIGHT_RAIN',
    'MODERATE_RAIN',
    'HEAVY_RAIN',
    'RAIN_SHOWERS',
    'SCATTERED_SHOWERS',
    'SHOWERS',
    'THUNDERSTORM',
    'SCATTERED_THUNDERSTORMS',
    'HEAVY_THUNDERSTORMS'
  ]);

  const WEATHER_EMOJI = {
    CLEAR: '☀️',
    MOSTLY_CLEAR: '🌤️',
    PARTLY_CLOUDY: '🌤️',
    MOSTLY_CLOUDY: '⛅',
    CLOUDY: '☁️',
    FOG: '🌫️',
    LIGHT_RAIN: '🌦️',
    DRIZZLE: '🌦️',
    SCATTERED_SHOWERS: '🌦️',
    RAIN_SHOWERS: '🌧️',
    SHOWERS: '🌧️',
    MODERATE_RAIN: '🌧️',
    HEAVY_RAIN: '🌧️',
    THUNDERSTORM: '⛈️',
    SCATTERED_THUNDERSTORMS: '⛈️',
    LIGHT_SNOW: '🌨️',
    SNOW: '❄️',
    HEAVY_SNOW: '❄️'
  };

  const LOCATION_NOTES = {
    'arboretum-farmers-market': {
      clear: 'Quick parking makes this an easy weekend stop.',
      rain: 'Quick parking makes Arboretum an easy rainy-day stop.'
    },
    'parmer-ranch-park': {
      clear: 'Grab coffee from Lost Horse and enjoy the park.',
      rain: 'Coffee, fresh bread, and neighborhood support—rain or shine.'
    },
    'bee-cave-farmers-market': {
      clear: 'A great morning to explore the Galleria.',
      rain: 'Bring an umbrella and show the Galleria vendors some rainy-day love.'
    },
    'parmer-ranch-porch-pickup': {
      clear: 'Your labeled order will be waiting on the porch.',
      rain: 'Rain or shine, your order will be waiting on the covered porch.'
    }
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const markerColor = (handle) => LOCATION_COLORS[handle] || '#2B3647';

  const getTexasNow = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      date: new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day))),
      hour: Number(values.hour),
      minute: Number(values.minute)
    };
  };

  const isoDate = (date) =>
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
      date.getUTCDate()
    ).padStart(2, '0')}`;

  const parseBlockedDates = (value) => {
    if (!value) return [];
    return String(value).match(/\d{4}-\d{2}-\d{2}/g) || [];
  };

  const parseTimeToMinutes = (value) => {
    if (!value) return null;
    const match = String(value)
      .trim()
      .toUpperCase()
      .match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/);

    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridiem = match[3];

    if (meridiem === 'PM' && hour !== 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;

    return hour * 60 + minute;
  };

  const getEndMinutes = (timeWindow) => {
    if (!timeWindow) return null;
    const parts = String(timeWindow).split(/[–—-]/);
    return parseTimeToMinutes(parts[parts.length - 1]);
  };


  const getStartMinutes = (timeWindow) => {
    if (!timeWindow) return null;
    const parts = String(timeWindow).split(/[–—-]/);
    return parseTimeToMinutes(parts[0]);
  };

  const getEventHours = (location) => {
    const value = String(location.timeWindow || location.displaySchedule || '');
    const parts = value.split(/[–—-]/);
    if (parts.length < 2) return { startHour: 10, endHour: 14 };

    const startMinutes = parseTimeToMinutes(parts[0]);
    const endMinutes = parseTimeToMinutes(parts[parts.length - 1]);

    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      return { startHour: 10, endHour: 14 };
    }

    return {
      startHour: Math.floor(startMinutes / 60),
      endHour: Math.ceil(endMinutes / 60)
    };
  };

  const getNextOccurrence = (location) => {
    const weekdayNames = String(location.activeWeekdays || '')
      .toLowerCase()
      .split(/[,\n|]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    const targetDays = weekdayNames
      .map((name) => WEEKDAYS[name])
      .filter((value) => Number.isInteger(value));

    if (!targetDays.length) return null;

    const texas = getTexasNow();
    const currentDay = texas.date.getUTCDay();
    const nowMinutes = texas.hour * 60 + texas.minute;
    const eventWindow =
      location.timeWindow || location.displaySchedule || '';
    const endMinutes = getEndMinutes(eventWindow) ?? 14 * 60;

    const candidates = targetDays.map((targetDay) => {
      let offset = (targetDay - currentDay + 7) % 7;
      if (offset === 0 && endMinutes !== null && nowMinutes >= endMinutes) offset = 7;

      const date = new Date(texas.date);
      date.setUTCDate(date.getUTCDate() + offset);
      return date;
    });

    const nextDate = candidates.sort((a, b) => a - b)[0];

    if (location.startDate) {
      const start = new Date(`${location.startDate}T00:00:00Z`);
      while (nextDate < start) nextDate.setUTCDate(nextDate.getUTCDate() + 7);
    }

    if (location.endDate) {
      const end = new Date(`${location.endDate}T23:59:59Z`);
      if (nextDate > end) return null;
    }

    const startMinutes = getStartMinutes(eventWindow) ?? 0;
    const minutesUntilStart =
      ((nextDate - texas.date) / 86400000) * 1440 +
      startMinutes -
      nowMinutes;

    if (minutesUntilStart > 7 * 24 * 60) return null;

    const dateIso = isoDate(nextDate);

    return {
      date: nextDate,
      iso: dateIso,
      blocked: parseBlockedDates(location.blockedDates).includes(dateIso)
    };
  };

  const formatDateChip = (date) => {
    const dayNumber = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      day: 'numeric'
    }).format(date);

    const dayName = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'short'
    }).format(date);

    const monthShort = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short'
    }).format(date);

    return {
      dayNumber,
      dayName,
      monthShort,
      short: `${dayName}, ${monthShort} ${dayNumber}`,
      full: new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      }).format(date)
    };
  };

  const toFahrenheit = (temperature) => {
    if (!temperature || typeof temperature.degrees !== 'number') return null;
    if (temperature.unit === 'FAHRENHEIT') return Math.round(temperature.degrees);
    return Math.round((temperature.degrees * 9) / 5 + 32);
  };

  const forecastType = (forecast) =>
    forecast?.eventWindow
      ? forecast?.weatherCondition?.type || ''
      : forecast?.daytimeForecast?.weatherCondition?.type || '';

  const forecastDescription = (forecast) =>
    forecast?.eventWindow
      ? forecast?.weatherCondition?.description?.text || 'Forecast available'
      : forecast?.daytimeForecast?.weatherCondition?.description?.text || 'Forecast available';

  const forecastRainProbability = (forecast) => {
    if (forecast?.eventWindow) {
      const probability = Number(forecast.precipitationProbabilityPercent);
      return Number.isFinite(probability) ? probability : null;
    }

    const probability = Number(
      forecast?.daytimeForecast?.precipitation?.probability?.percent
    );
    return Number.isFinite(probability) ? probability : null;
  };

  const forecastTemperatureLabel = (forecast) => {
    if (forecast?.eventWindow) {
      const low = toFahrenheit(forecast.minTemperature);
      const high = toFahrenheit(forecast.maxTemperature);
      if (low !== null && high !== null) return low === high ? `${low}°` : `${low}–${high}°`;
      if (high !== null) return `${high}°`;
      if (low !== null) return `${low}°`;
      return '';
    }

    const high = toFahrenheit(forecast?.maxTemperature);
    return high !== null ? `${high}°` : '';
  };

  const isRainyForecast = (forecast) => {
    if (forecast?.eventWindow && typeof forecast.rainy === 'boolean') {
      return forecast.rainy;
    }

    const type = forecastType(forecast);
    const probability = forecastRainProbability(forecast) || 0;
    return probability >= 40 || (RAIN_TYPES.has(type) && probability >= 20);
  };

  const weatherEmoji = (type) => WEATHER_EMOJI[type] || '🌤️';

  const loadGoogleMaps = (() => {
    let promise;

    return (apiKey) => {
      if (!apiKey) return Promise.reject(new Error('Google Maps API key is missing.'));
      if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
      if (promise) return promise;

      promise = new Promise((resolve, reject) => {
        const callbackName = `csGoogleMapsReady_${Date.now()}`;
        window[callbackName] = () => {
          delete window[callbackName];
          resolve(window.google.maps);
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
          apiKey
        )}&v=weekly&loading=async&callback=${callbackName}&region=US&language=en`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps could not be loaded.'));
        document.head.appendChild(script);
      });

      return promise;
    };
  })();

  roots.forEach((root) => {
    const dataScript = root.querySelector('[data-cs-location-data]');
    if (!dataScript) return;

    let locations = [];
    try {
      locations = JSON.parse(dataScript.textContent || '[]');
    } catch (error) {
      console.error('Cold Sugar locations data could not be parsed.', error);
      return;
    }

    const settings = {
      apiKey: root.dataset.googleApiKey || '',
      mapId: root.dataset.googleMapId || 'DEMO_MAP_ID',
      showWeather: root.dataset.showWeather === 'true',
      showMap: root.dataset.showMap === 'true',
      showFavorites: root.dataset.showFavorites === 'true',
      localShopUrl: root.dataset.localShopUrl || '/pages/about-us?view=shop&mode=local',
      weatherEndpoint: root.dataset.weatherEndpoint || ''
    };

    const PARMER_RANCH_BAKEHOUSE_ADDRESS =
      '2380 Sawdust Dr, Georgetown, TX 78633';

    const scheduleContainer = root.querySelector('[data-cs-schedule]');
    const rankingContainer = root.querySelector('[data-cs-ranking]');
    const mapElement = root.querySelector('[data-cs-map]');
    const weatherElement = root.querySelector('[data-cs-weather]');
    const weatherGrid = root.querySelector('[data-cs-weather-grid]');
    const weatherSummary = root.querySelector('[data-cs-weather-summary]');
    const rainMessage = root.querySelector('[data-cs-rain-message]');
    const drawerEvents = root.querySelector('[data-cs-drawer-events]');
    const drawerRain = root.querySelector('[data-cs-drawer-rain]');
    const toast = root.querySelector('[data-cs-toast]');
    const nearbyStatus = root.querySelector('[data-cs-nearby-status]');
    const addressInput = root.querySelector('[data-cs-address-input]');
    const findButton = root.querySelector('[data-cs-find-nearby]');
    const useLocationButton = root.querySelector('[data-cs-use-location]');

    const pickupLocations = locations
      .filter((location) => String(location.category || '').toLowerCase() === 'pickup')
      .map((location) => ({ ...location, occurrence: getNextOccurrence(location) }))
      .filter((location) => location.occurrence)
      .sort((a, b) => {
        const dateDifference = a.occurrence.date - b.occurrence.date;
        if (dateDifference) return dateDifference;

        const aStart =
          getStartMinutes(a.timeWindow || a.displaySchedule) ?? 1440;
        const bStart =
          getStartMinutes(b.timeWindow || b.displaySchedule) ?? 1440;
        const startDifference = aStart - bStart;

        return (
          startDifference ||
          Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
        );
      });

    const publicPickupLocations = pickupLocations.filter((location) => location.mapEnabled);

    const scheduleCopy = (location) => {
      if (location.occurrence.blocked) {
        return `We won’t be at ${location.name} on this date. Please choose another Cold Sugar stop.`;
      }

      const copy = {
        'arboretum-farmers-market': 'A quick, convenient Saturday stop in North Austin.',
        'bee-cave-farmers-market': 'A relaxed Sunday outing for the whole family.',
        'parmer-ranch-park': 'A personal neighborhood stop beside Lost Horse Coffee.',
        'parmer-ranch-porch-pickup':
          'Thursday Porch Shop with a limited live menu and self-serve pickup.'
      };

      return copy[location.handle] || location.publicDescription || '';
    };

    const buildLocationOrderUrl = (location) => {
      if (!location?.handle) return settings.localShopUrl;

      if (location.handle === 'parmer-ranch-porch-pickup') {
        return '/pages/parmer-ranch-porch-pickup';
      }

      const url = new URL(settings.localShopUrl, window.location.origin);
      const pickupSubtype = String(
        location.pickupSubtype || location.pickup_subtype || location.subtype || ''
      ).toLowerCase();

      url.searchParams.set('view', 'shop');
      url.searchParams.set('mode', 'local');
      url.searchParams.set('gateway', 'skip');
      url.searchParams.set(
        'shopping_path',
        pickupSubtype === 'bakehouse_order_ahead' ||
          location.handle === 'cold-sugar-bakehouse-parmer-ranch'
          ? 'bakehouse_order_ahead'
          : 'market_pickup'
      );
      url.searchParams.set('fulfillment', location.handle);

      return `${url.pathname}${url.search}${url.hash}`;
    };

    const renderSchedule = () => {
      if (!scheduleContainer) return;

      const scheduleLocations = [...pickupLocations].sort(
        (a, b) =>
          Number(a.occurrence.blocked) -
          Number(b.occurrence.blocked)
      );

      scheduleContainer.innerHTML = scheduleLocations
        .map((location) => {
          const date = formatDateChip(location.occurrence.date);
          const canOrder = location.availableInCart && !location.occurrence.blocked;
          const isPorchShop =
            location.handle === 'parmer-ranch-porch-pickup';

          const scheduleName = isPorchShop
            ? 'Cold Sugar Bakehouse'
            : location.name;

          const scheduleAddress = isPorchShop
            ? PARMER_RANCH_BAKEHOUSE_ADDRESS
            : location.publicAddress || location.cityState || '';

          const directionsHref = isPorchShop
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                PARMER_RANCH_BAKEHOUSE_ADDRESS
              )}&travelmode=driving`
            : location.directionsLink;

          const directions = directionsHref
            ? `<a class="cs-locations__button cs-locations__button--ghost" href="${escapeHtml(
                directionsHref
              )}" target="_blank" rel="noopener">Directions</a>`
            : '';

          const orderButtonLabel = isPorchShop
            ? 'Porch Shop'
            : 'Order for pickup';

          const orderButton = canOrder
            ? `<a class="cs-locations__button" href="${escapeHtml(
                buildLocationOrderUrl(location)
              )}">${escapeHtml(orderButtonLabel)}</a>`
            : '';

          return `
            <article class="cs-schedule-card${
              location.occurrence.blocked ? ' cs-schedule-card--cancelled' : ''
            }" data-schedule-handle="${escapeHtml(location.handle)}">
              <div class="cs-schedule-card__date">
                <strong>${escapeHtml(date.dayNumber)}</strong>
                <span>${escapeHtml(date.dayName)} · ${escapeHtml(
                  date.monthShort
                )}</span>
              </div>
              <div class="cs-schedule-card__content">
                <p class="cs-schedule-card__city">${escapeHtml(location.cityState || '')}</p>
                ${
                  location.occurrence.blocked
                    ? '<span class="cs-schedule-card__badge">Schedule change</span>'
                    : ''
                }
                <h3>${escapeHtml(scheduleName)}</h3>
                <p class="cs-schedule-card__copy">${escapeHtml(scheduleCopy(location))}</p>
                <div class="cs-schedule-card__meta">
                  <span><b aria-hidden="true">◷</b>${escapeHtml(
                    location.timeWindow || location.displaySchedule || ''
                  )}</span>
                  <span><b aria-hidden="true">⌖</b>${escapeHtml(
                    scheduleAddress
                  )}</span>
                </div>
                <div class="cs-schedule-card__weather" data-weather-snippet="${escapeHtml(
                  location.handle
                )}"></div>
                <div class="cs-schedule-card__actions">${orderButton}${directions}</div>
              </div>
            </article>
          `;
        })
        .join('');
    };

    const renderDrawerCard = (location) => {
      const date = formatDateChip(location.occurrence.date);
      const blocked = location.occurrence.blocked;

      const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        weekday: 'long'
      }).format(location.occurrence.date);

      return `
        <article class="cs-week-drawer__event${
          blocked ? ' is-cancelled' : ''
        }" data-drawer-handle="${escapeHtml(location.handle)}">
          <small>${escapeHtml(date.full)} · ${escapeHtml(location.timeWindow || '')}</small>
          <h3>${escapeHtml(location.name)}</h3>
          <p>${escapeHtml(location.cityState || '')}</p>

          ${
            blocked
              ? `<div class="cs-week-drawer__cancelled-copy">
                  <strong>Not attending this ${escapeHtml(weekday)}</strong>
                  <span>Cold Sugar will not be at this stop on this date.</span>
                </div>`
              : `<div class="cs-week-drawer__weather" data-drawer-weather="${escapeHtml(
                  location.handle
                )}"></div>
                <p class="cs-week-drawer__rain-note" data-drawer-rain-note="${escapeHtml(
                  location.handle
                )}"></p>`
          }
        </article>
      `;
    };

    const renderDrawerGroup = (label, items) => `
      <section class="cs-week-drawer__group">
        <p class="cs-week-drawer__group-label">${escapeHtml(label)}</p>
        <div class="cs-week-drawer__group-events">
          ${items.map(renderDrawerCard).join('')}
        </div>
      </section>
    `;

    const renderDrawer = () => {
      if (!drawerEvents) return;

      if (!pickupLocations.length) {
        drawerEvents.innerHTML =
          '<p class="cs-week-drawer__empty">No upcoming locations are currently listed. View the full schedule for updates.</p>';
        return;
      }

      const attending = pickupLocations.filter(
        (location) => !location.occurrence.blocked
      );

      const notAttending = pickupLocations.filter(
        (location) => location.occurrence.blocked
      );

      const sections = [];

      if (!attending.length && notAttending.length) {
        sections.push(`
          <div class="cs-week-drawer__week-status cs-week-drawer__week-status--closed">
            <strong>No upcoming Cold Sugar stops</strong>
            <span>There are no scheduled Cold Sugar stops in the next 7 days. Check the full schedule for updates.</span>
          </div>
        `);
      }

      if (attending.length) {
        sections.push(
          notAttending.length
            ? renderDrawerGroup('Upcoming stops', attending)
            : attending.map(renderDrawerCard).join('')
        );
      }

      if (notAttending.length) {
        sections.push(
          renderDrawerGroup('Schedule changes', notAttending)
        );
      }

      drawerEvents.innerHTML = sections.join('');
    };

    const rankingCardMarkup = (location, index, distanceMarkup = '') => `
      <button
        type="button"
        class="cs-rank-card${index === 0 ? ' is-active' : ''}"
        data-rank-handle="${escapeHtml(location.handle)}"
        style="--cs-location-color:${markerColor(location.handle)}"
      >
        <span>
          <h3>${escapeHtml(location.name)}</h3>
          <p>${escapeHtml(location.displaySchedule || location.timeWindow || '')}</p>
        </span>
        <span class="cs-rank-card__distance">
          ${distanceMarkup || '<strong>Explore</strong><span>View on map</span>'}
        </span>
      </button>
    `;

    let map;
    let geocoder;
    let RouteMatrix;
    let AdvancedMarkerElement;
    let originMarker;
    const markerByHandle = new Map();
    const coordinatesByHandle = new Map();

    const setActiveMarker = (handle) => {
      markerByHandle.forEach((stored, markerHandle) => {
        stored.element?.classList.toggle('is-active', markerHandle === handle);
      });
    };

    const selectMapLocation = (handle, zoomToLocation = false) => {
      root.querySelectorAll('[data-rank-handle]').forEach((card) => {
        card.classList.toggle('is-active', card.dataset.rankHandle === handle);
      });

      setActiveMarker(handle);

      const coordinates = coordinatesByHandle.get(handle);
      if (map && coordinates && zoomToLocation) {
        map.panTo(coordinates);
        map.setZoom(12);
      }
    };

    const bindRankingCards = () => {
      root.querySelectorAll('[data-rank-handle]').forEach((card) => {
        if (card.dataset.csMapBound === 'true') return;
        card.dataset.csMapBound = 'true';
        card.addEventListener('click', () => {
          selectMapLocation(card.dataset.rankHandle, true);
        });
      });
    };

    const renderDefaultRanking = () => {
      if (!rankingContainer) return;
      rankingContainer.innerHTML = publicPickupLocations
        .map((location, index) => rankingCardMarkup(location, index))
        .join('');
      bindRankingCards();
    };

    renderSchedule();
    renderDrawer();
    renderDefaultRanking();

    const showToast = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(showToast.timer);
      showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
    };

    const favoriteKey = 'coldSugarFavoriteLocation';

    const applyFavorite = (handle) => {
      root.querySelectorAll('[data-cs-location-card]').forEach((card) => {
        card.classList.toggle('is-favorite', card.dataset.locationHandle === handle);
      });

      root.querySelectorAll('[data-cs-favorite]').forEach((button) => {
        const active = button.dataset.locationHandle === handle;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          active ? 'Remove favorite location' : 'Set as favorite location'
        );
        const symbol = button.querySelector('[aria-hidden="true"]');
        if (symbol) symbol.textContent = active ? '★' : '☆';
      });
    };

    if (settings.showFavorites) {
      try {
        const savedFavorite = window.localStorage.getItem(favoriteKey);
        if (savedFavorite) applyFavorite(savedFavorite);
      } catch (_error) {
        // Local storage may be unavailable in private browsing.
      }

      root.querySelectorAll('[data-cs-favorite]').forEach((button) => {
        button.addEventListener('click', () => {
          const handle = button.dataset.locationHandle;
          const location = locations.find((item) => item.handle === handle);
          let current = '';

          try {
            current = window.localStorage.getItem(favoriteKey) || '';
          } catch (_error) {
            // Continue with an in-page favorite state.
          }

          if (current === handle) {
            try {
              window.localStorage.removeItem(favoriteKey);
            } catch (_error) {
              // Ignore storage failures.
            }
            applyFavorite('');
            showToast('Favorite location cleared.');
            return;
          }

          try {
            window.localStorage.setItem(favoriteKey, handle);
          } catch (_error) {
            // Ignore storage failures.
          }
          applyFavorite(handle);
          showToast(`${location?.name || 'This location'} is now your favorite Cold Sugar stop.`);
        });
      });
    }

    const trigger = root.querySelector('[data-cs-week-trigger]');
    const overlay = root.querySelector('[data-cs-week-overlay]');
    const drawer = root.querySelector('[data-cs-week-drawer]');
    const closeButton = root.querySelector('[data-cs-week-close]');

    const openDrawer = () => {
      if (!overlay || !drawer || !trigger) return;
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cs-week-drawer-open');
      closeButton?.focus();
    };

    const closeDrawer = () => {
      if (!overlay || !drawer || !trigger) return;
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('cs-week-drawer-open');
      window.setTimeout(() => {
        overlay.hidden = true;
        trigger.focus();
      }, 260);
    };

    trigger?.addEventListener('click', openDrawer);
    closeButton?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', (event) => {
      if (event.target === overlay) closeDrawer();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay && !overlay.hidden) closeDrawer();
    });

    const createMarkerContent = (location, index) => {
      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'cs-google-marker';
      element.style.setProperty('--cs-marker-color', markerColor(location.handle));
      element.title = location.name;
      element.setAttribute('aria-label', `Show ${location.name}`);

      // The enhancement file removes this fallback number and inserts the
      // location icon. Keeping it here makes the base map usable on its own.
      element.innerHTML = `<span class="cs-google-marker__number">${index + 1}</span>`;
      return element;
    };

    const createOriginMarkerContent = () => {
      const element = document.createElement('div');
      element.className = 'cs-google-marker cs-google-marker--origin';
      element.setAttribute('aria-label', 'Your searched location');
      element.innerHTML = '<span class="cs-google-marker__number">You</span>';
      return element;
    };

    const readExplicitCoordinates = (location) => {
      const latitude = Number(location.latitude ?? location.mapLatitude);
      const longitude = Number(location.longitude ?? location.mapLongitude);

      if (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        Math.abs(latitude) <= 90 &&
        Math.abs(longitude) <= 180 &&
        !(latitude === 0 && longitude === 0)
      ) {
        return { lat: latitude, lng: longitude };
      }

      return null;
    };

    const geocodeLocation = async (location) => {
      const fixed = FIXED_COORDINATES[location.handle];
      if (fixed) return fixed;

      const explicit = readExplicitCoordinates(location);
      if (explicit) return explicit;

      if (!geocoder) return null;

      const queryCandidates = [
        location.mapQuery,
        location.publicAddress,
        location.name && location.cityState
          ? `${location.name}, ${location.cityState}`
          : ''
      ]
        .map((value) => String(value || '').trim())
        .filter((value, index, list) => value && list.indexOf(value) === index);

      for (const query of queryCandidates) {
        const cacheKey = `csGeocode:${query}`;

        try {
          const cached = JSON.parse(window.localStorage.getItem(cacheKey) || 'null');
          if (
            Number.isFinite(cached?.lat) &&
            Number.isFinite(cached?.lng) &&
            !(cached.lat === 0 && cached.lng === 0)
          ) {
            return cached;
          }
        } catch (_error) {
          // Ignore invalid cache.
        }

        try {
          const response = await geocoder.geocode({
            address: query,
            region: 'us',
            componentRestrictions: { country: 'US' }
          });

          const result = response.results?.[0];
          if (!result) continue;

          const coordinates = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          };

          if (
            !Number.isFinite(coordinates.lat) ||
            !Number.isFinite(coordinates.lng) ||
            (coordinates.lat === 0 && coordinates.lng === 0)
          ) {
            continue;
          }

          try {
            window.localStorage.setItem(cacheKey, JSON.stringify(coordinates));
          } catch (_error) {
            // Local storage may be unavailable.
          }

          return coordinates;
        } catch (error) {
          console.warn(`Could not geocode ${location.name} using “${query}”.`, error);
        }
      }

      console.warn(`No usable map coordinates were found for ${location.name}.`);
      return null;
    };

    const geocodeCustomerInput = async (value) => {
      const selectedLat = Number(addressInput?.dataset.csSelectedLat);
      const selectedLng = Number(addressInput?.dataset.csSelectedLng);

      if (Number.isFinite(selectedLat) && Number.isFinite(selectedLng)) {
        return {
          coordinates: { lat: selectedLat, lng: selectedLng },
          formattedAddress: value
        };
      }

      if (!geocoder) throw new Error('GEOCODER_NOT_READY');

      const trimmed = String(value || '').trim();
      const request = /^\d{5}(?:-\d{4})?$/.test(trimmed)
        ? {
            address: `${trimmed}, USA`,
            region: 'us',
            componentRestrictions: { country: 'US', postalCode: trimmed }
          }
        : {
            address: trimmed,
            region: 'us',
            componentRestrictions: { country: 'US' }
          };

      const response = await geocoder.geocode(request);
      const result = response.results?.[0];
      if (!result) throw new Error('ADDRESS_NOT_FOUND');

      return {
        coordinates: {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng()
        },
        formattedAddress: result.formatted_address || trimmed
      };
    };

    const fitMapToLocations = (extraCoordinate = null) => {
      if (!map || !window.google?.maps?.LatLngBounds) return;
      const bounds = new google.maps.LatLngBounds();
      coordinatesByHandle.forEach((coordinate) => bounds.extend(coordinate));
      if (extraCoordinate) bounds.extend(extraCoordinate);
      if (!bounds.isEmpty()) map.fitBounds(bounds, 70);
    };

    const showOriginMarker = (coordinates) => {
      if (!map || !AdvancedMarkerElement) return;
      if (originMarker) originMarker.map = null;
      originMarker = new AdvancedMarkerElement({
        map,
        position: coordinates,
        title: 'Your searched location',
        content: createOriginMarkerContent(),
        zIndex: 999
      });
    };

    const rankByOrigin = async (origin, formattedAddress = 'your location') => {
      if (!RouteMatrix || !coordinatesByHandle.size) {
        throw new Error('ROUTES_NOT_READY');
      }

      const destinationLocations = publicPickupLocations.filter((location) =>
        coordinatesByHandle.has(location.handle)
      );
      const destinations = destinationLocations.map((location) =>
        coordinatesByHandle.get(location.handle)
      );

      const response = await RouteMatrix.computeRouteMatrix({
        origins: [origin],
        destinations,
        travelMode: 'DRIVING',
        fields: ['durationMillis', 'distanceMeters', 'condition']
      });

      const items = response.matrix?.rows?.[0]?.items || [];
      const ranked = destinationLocations
        .map((location, index) => {
          const item = items[index];
          const routeExists = !item?.condition || String(item.condition) === 'ROUTE_EXISTS';
          return {
            location,
            durationMillis:
              routeExists && typeof item?.durationMillis === 'number'
                ? item.durationMillis
                : Number.POSITIVE_INFINITY,
            distanceMeters:
              routeExists && typeof item?.distanceMeters === 'number'
                ? item.distanceMeters
                : Number.POSITIVE_INFINITY
          };
        })
        .filter((item) => Number.isFinite(item.distanceMeters))
        .sort((a, b) => a.durationMillis - b.durationMillis);

      if (!ranked.length) throw new Error('NO_ROUTES_FOUND');

      if (rankingContainer) {
        rankingContainer.innerHTML = ranked
          .map(({ location, durationMillis, distanceMeters }, index) => {
            const minutes = Math.max(1, Math.round(durationMillis / 60000));
            const miles = distanceMeters / 1609.344;
            return rankingCardMarkup(
              location,
              index,
              `<strong>${minutes} min</strong><span>${miles.toFixed(miles < 10 ? 1 : 0)} miles</span>`
            );
          })
          .join('');
      }

      // Ranking numbers were intentionally removed from the map markers. Do
      // not try to update a number element that no longer exists.
      bindRankingCards();

      const closest = ranked[0].location;
      selectMapLocation(closest.handle, false);
      showOriginMarker(origin);
      fitMapToLocations(origin);

      if (nearbyStatus) {
        nearbyStatus.textContent = `${closest.name} is the closest Cold Sugar stop from ${formattedAddress}.`;
      }
    };

    const fetchWeatherForLocation = async (location, coordinates) => {
      if (!settings.weatherEndpoint || !coordinates) return null;

      const eventHours = getEventHours(location);
      const cacheKey = `csWeatherV2:${location.handle}:${location.occurrence?.iso || ''}:${eventHours.startHour}-${eventHours.endHour}`;
      try {
        const cached = JSON.parse(window.localStorage.getItem(cacheKey) || 'null');
        if (cached?.savedAt && Date.now() - cached.savedAt < 30 * 60 * 1000) {
          return cached.forecast || null;
        }
      } catch (_error) {
        // Ignore invalid cache.
      }

      const endpoint = new URL(settings.weatherEndpoint, window.location.origin);
      endpoint.searchParams.set('lat', String(coordinates.lat));
      endpoint.searchParams.set('lng', String(coordinates.lng));
      endpoint.searchParams.set('date', location.occurrence?.iso || '');
      endpoint.searchParams.set('handle', location.handle);
      endpoint.searchParams.set('startHour', String(eventHours.startHour));
      endpoint.searchParams.set('endHour', String(eventHours.endHour));

      const response = await fetch(endpoint.toString(), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`WEATHER_${response.status}`);

      const data = await response.json();
      const forecast = data.forecast || data.eventForecast || data.dailyForecast || data;

      try {
        window.localStorage.setItem(
          cacheKey,
          JSON.stringify({ savedAt: Date.now(), forecast })
        );
      } catch (_error) {
        // Ignore storage failures.
      }

      return forecast || null;
    };

    const updateWeather = (weatherResults) => {
      if (!weatherElement || !weatherGrid) return;

      const marketResults = weatherResults.filter(
        ({ location, forecast }) =>
          forecast && ['farmers_market', 'neighborhood_park'].includes(location.subtype)
      );

      if (!marketResults.length) return;

      const anyRain = marketResults.some(({ forecast }) => isRainyForecast(forecast));
      weatherElement.hidden = false;

      if (rainMessage) {
        rainMessage.hidden = !anyRain;
        rainMessage.style.display = anyRain ? '' : 'none';
      }

      if (drawerRain) {
        drawerRain.hidden = !anyRain;
        drawerRain.style.display = anyRain ? '' : 'none';
      }

      if (weatherSummary) {
        weatherSummary.textContent = anyRain
          ? 'A few showers may overlap market hours, but our outdoor markets and local vendors plan to be there rain or shine.'
          : 'The market-hour forecast is looking good for fresh bread, local shopping, and a little time outside.';
      }

      weatherGrid.innerHTML = marketResults
        .map(({ location, forecast }) => {
          const rainy = isRainyForecast(forecast);
          const type = forecastType(forecast);
          const description = forecastDescription(forecast);
          const temperature = forecastTemperatureLabel(forecast);
          const probability = forecastRainProbability(forecast);
          const date = formatDateChip(location.occurrence.date);
          const note = LOCATION_NOTES[location.handle]?.[rainy ? 'rain' : 'clear'] || '';

          return `
            <article class="cs-weather-card">
              <span class="cs-weather-card__day">${escapeHtml(date.short)} · ${escapeHtml(
                location.name
              )}</span>
              <div class="cs-weather-card__main">
                <span class="cs-weather-card__icon" aria-hidden="true">${weatherEmoji(type)}</span>
                <span>
                  <strong class="cs-weather-card__temp">${escapeHtml(temperature)}</strong>
                  <small>${escapeHtml(description)}${
                    probability !== null ? ` · ${probability}% rain during market hours` : ''
                  }</small>
                </span>
              </div>
              <p>${escapeHtml(note)}</p>
            </article>
          `;
        })
        .join('');

      weatherResults.forEach(({ location, forecast }) => {
        if (!forecast) return;

        const rainy = isRainyForecast(forecast);
        const type = forecastType(forecast);
        const description = forecastDescription(forecast);
        const temperature = forecastTemperatureLabel(forecast);
        const snippet = `${weatherEmoji(type)} ${temperature ? `${temperature} · ` : ''}${description}`;
        const note = LOCATION_NOTES[location.handle]?.[rainy ? 'rain' : 'clear'] || '';

        root
          .querySelectorAll(`[data-weather-snippet="${CSS.escape(location.handle)}"]`)
          .forEach((element) => {
            element.textContent = snippet;
            element.classList.add('is-ready');
          });

        root
          .querySelectorAll(`[data-drawer-weather="${CSS.escape(location.handle)}"]`)
          .forEach((element) => {
            element.textContent = snippet;
            element.classList.add('is-ready');
            element.classList.toggle('is-rainy', rainy);
          });

        root
          .querySelectorAll(`[data-drawer-rain-note="${CSS.escape(location.handle)}"]`)
          .forEach((element) => {
            element.textContent = rainy ? note : '';
            element.classList.toggle('is-ready', rainy);
          });
      });
    };

    const initGoogleFeatures = async () => {
      if (!settings.apiKey) {
        if (nearbyStatus) {
          nearbyStatus.textContent =
            'Add the Google Maps Platform key in the theme editor to activate this tool.';
        }
        return;
      }

      try {
        await loadGoogleMaps(settings.apiKey);
        const [{ Map }, markerLibrary, routesLibrary] = await Promise.all([
          google.maps.importLibrary('maps'),
          google.maps.importLibrary('marker'),
          google.maps.importLibrary('routes')
        ]);

        AdvancedMarkerElement = markerLibrary.AdvancedMarkerElement;
        RouteMatrix = routesLibrary.RouteMatrix;
        geocoder = new google.maps.Geocoder();

        if (settings.showMap && mapElement) {
          mapElement.innerHTML = '';
          map = new Map(mapElement, {
            center: { lat: 30.48, lng: -97.82 },
            zoom: 9,
            mapId: settings.mapId || 'DEMO_MAP_ID',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            clickableIcons: false,
            gestureHandling: 'cooperative'
          });
        }

        const geocoded = await Promise.all(
          publicPickupLocations.map(async (location) => ({
            location,
            coordinates: await geocodeLocation(location)
          }))
        );

        geocoded.forEach(({ location, coordinates }, index) => {
          if (!coordinates) return;
          coordinatesByHandle.set(location.handle, coordinates);

          if (map) {
            const content = createMarkerContent(location, index);
            const marker = new AdvancedMarkerElement({
              map,
              position: coordinates,
              title: location.name,
              content,
              zIndex: 100 + index
            });

            content.addEventListener('click', () => {
              selectMapLocation(location.handle, true);
            });

            markerByHandle.set(location.handle, { marker, element: content });
          }
        });

        fitMapToLocations();
        bindRankingCards();

        if (settings.showWeather && settings.weatherEndpoint) {
          const weatherResults = await Promise.all(
            pickupLocations.map(async (location) => {
              let coordinates = coordinatesByHandle.get(location.handle);
              if (!coordinates && location.handle === 'parmer-ranch-porch-pickup') {
                coordinates = coordinatesByHandle.get('parmer-ranch-park');
              }

              try {
                const forecast = await fetchWeatherForLocation(location, coordinates);
                return { location, forecast };
              } catch (error) {
                console.warn(`Weather unavailable for ${location.name}.`, error);
                return { location, forecast: null };
              }
            })
          );
          updateWeather(weatherResults);
        }

        if (nearbyStatus) {
          nearbyStatus.textContent =
            'Enter your address or ZIP code to compare estimated drive times.';
        }
      } catch (error) {
        console.error('Cold Sugar map setup failed.', error);
        if (nearbyStatus) nearbyStatus.textContent = 'The map tools are temporarily unavailable.';
      }
    };

    findButton?.addEventListener('click', async () => {
      const address = addressInput?.value.trim();
      if (!address) {
        if (nearbyStatus) nearbyStatus.textContent = 'Enter an address or ZIP code first.';
        addressInput?.focus();
        return;
      }

      if (!geocoder || !RouteMatrix) {
        if (nearbyStatus) {
          nearbyStatus.textContent = 'The travel-time tool is still loading. Try again in a moment.';
        }
        return;
      }

      if (nearbyStatus) nearbyStatus.textContent = 'Finding that location…';
      findButton.disabled = true;

      try {
        const result = await geocodeCustomerInput(address);
        if (nearbyStatus) nearbyStatus.textContent = 'Comparing estimated drive times…';
        await rankByOrigin(result.coordinates, result.formattedAddress);
      } catch (error) {
        console.error('Cold Sugar address search failed.', error);

        if (!nearbyStatus) return;
        if (error.message === 'ADDRESS_NOT_FOUND') {
          nearbyStatus.textContent =
            'We couldn’t find that address or ZIP code. Try a complete street address or a five-digit ZIP.';
        } else if (error.message === 'NO_ROUTES_FOUND') {
          nearbyStatus.textContent =
            'We found your location, but couldn’t compare the driving routes. Please try again.';
        } else {
          nearbyStatus.textContent =
            'We found your location, but the drive-time comparison could not finish. Please try again.';
        }
      } finally {
        findButton.disabled = false;
      }
    });

    addressInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        findButton?.click();
      }
    });

    useLocationButton?.addEventListener('click', () => {
      if (!navigator.geolocation) {
        if (nearbyStatus) {
          nearbyStatus.textContent = 'Current-location search is not supported by this browser.';
        }
        return;
      }

      if (!RouteMatrix) {
        if (nearbyStatus) {
          nearbyStatus.textContent = 'The travel-time tool is still loading. Try again in a moment.';
        }
        return;
      }

      if (nearbyStatus) nearbyStatus.textContent = 'Finding your current location…';
      useLocationButton.disabled = true;

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            if (nearbyStatus) nearbyStatus.textContent = 'Comparing estimated drive times…';
            await rankByOrigin(coordinates, 'your current location');
          } catch (error) {
            console.error('Current-location route comparison failed.', error);
            if (nearbyStatus) {
              nearbyStatus.textContent =
                'We found your location, but couldn’t compare the driving routes. Please try again.';
            }
          } finally {
            useLocationButton.disabled = false;
          }
        },
        () => {
          if (nearbyStatus) {
            nearbyStatus.textContent =
              'We could not access your location. Check your browser permission or enter an address instead.';
          }
          useLocationButton.disabled = false;
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });

    void initGoogleFeatures();
  });
})();
